import { IconGoogleBusiness, IconGoogleSearch } from '../icons';
// =============================================================
// SearchConsoleCard  Phase 4
// Search Console Connection, Sync, and Analytics Display
// =============================================================

import React, { useState } from 'react';
import {
  SearchConsolePropertyDto,
  SearchConsolePropertyOption,
  SearchConsoleSummaryDto,
} from '../../lib/api';
import { ConnectionStatus } from './ConnectionStatus';
import { SyncButton } from './SyncButton';
import { PropertySelector } from './PropertySelector';

interface Props {
  googleConnected: boolean;
  connectedProperty: SearchConsolePropertyDto | null;
  availableProperties?: SearchConsolePropertyOption[];
  summary: SearchConsoleSummaryDto | null;
  onSelectProperty: (siteUrl: string) => Promise<void>;
  onSync: () => Promise<void>;
  loadingProperties: boolean;
  errorProperties?: string | null;
  syncing: boolean;
  onConnectGoogle: () => void;
}

export const SearchConsoleCard: React.FC<Props> = ({
  googleConnected,
  connectedProperty,
  availableProperties = [],
  summary,
  onSelectProperty,
  onSync,
  loadingProperties,
  errorProperties,
  syncing,
  onConnectGoogle,
}) => {
  const [showSelector, setShowSelector] = useState(false);
  const initializedRef = React.useRef(false);

  React.useEffect(() => {
    if (!initializedRef.current && googleConnected) {
      if (!connectedProperty) {
        setShowSelector(true);
      }
      initializedRef.current = true;
    }
  }, [connectedProperty, googleConnected]);

  const getStatus = () => {
    if (!googleConnected) return 'NOT_CONNECTED';
    if (!connectedProperty) return 'DISCONNECTED';
    return 'CONNECTED';
  };

  const getStatusLabel = () => {
    if (!googleConnected) return 'Google Not Connected';
    if (!connectedProperty) return 'Property Not Selected';
    return 'Property Selected';
  };

  return (
    <div
      className="card integration-dashboard-card"
      style={{
        padding: '24px',
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}
    >
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: '#eff6ff',
              border: '1px solid #dbeafe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.6rem',
            }}
          >
            <IconGoogleSearch style={{ width: 24, height: 24, color: '#34A853' }} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
                Google Search Console
              </h3>
              <span
                style={{
                  fontSize: '0.72rem',
                  padding: '2px 8px',
                  background: '#dbeafe',
                  color: '#1e40af',
                  borderRadius: '4px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Data Source: Google Search Console
              </span>
            </div>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              Search performance, impressions, organic clicks, and keyword queries
            </span>
          </div>
        </div>

        <ConnectionStatus status={getStatus()} label={getStatusLabel()} />
      </div>

      {!googleConnected ? (
        <div
          style={{
            padding: '20px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px dashed #cbd5e1',
            textAlign: 'center',
          }}
        >
          <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#475569' }}>
            Connect your Google account above to enable Google Search Console property selection and analytics sync.
          </p>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onConnectGoogle}
          >
            Connect Google Account First 
          </button>
        </div>
      ) : (
        <>
          {/* Property status & selector toggle */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}
          >
            <div>
              <span style={{ fontSize: '0.78rem', color: '#64748b', display: 'block' }}>Connected Property</span>
              <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>
                {connectedProperty ? connectedProperty.siteUrl : 'None selected'}
              </strong>
              {connectedProperty && (
                <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#059669' }}>
                  ({connectedProperty.propertyType}  Verified)
                </span>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowSelector(!showSelector)}
              >
                {showSelector ? 'Close Selector' : connectedProperty ? 'Change Property' : 'Select Property'}
              </button>

              {connectedProperty && (
                <SyncButton
                  onSync={onSync}
                  loading={syncing}
                  lastSyncedAt={connectedProperty.lastSyncedAt}
                  label="Sync Search Analytics (28d)"
                  sourceLabel="GSC"
                />
              )}
            </div>
          </div>

          {/* Collapsible Property Selector */}
          {showSelector && (
            <div style={{ marginBottom: '20px' }}>
              <PropertySelector
                connectedProperty={connectedProperty}
                availableProperties={availableProperties || []}
                onSelectProperty={async (url) => {
                  await onSelectProperty(url);
                  setShowSelector(false);
                }}
                loading={loadingProperties}
                error={errorProperties}
              />
            </div>
          )}

          {/* Metrics Display */}
          {connectedProperty && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>
                  Search Performance (Past 28 Days)
                </h4>
                {summary && (
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Period: {summary.dateRange.start} to {summary.dateRange.end}
                  </span>
                )}
              </div>

              {!summary || summary.totalImpressions === 0 ? (
                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', textAlign: 'center', color: '#64748b', fontSize: '0.88rem' }}>
                  No Search Console metrics synced yet. Click <strong>"Sync Search Analytics (28d)"</strong> to import search impressions and keyword queries.
                </div>
              ) : (
                <>
                  {/* Metric Summary Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    <div style={{ padding: '14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                      <span style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600, display: 'block' }}>TOTAL CLICKS</span>
                      <strong style={{ fontSize: '1.5rem', color: '#15803d' }}>{summary.totalClicks.toLocaleString()}</strong>
                      <span style={{ fontSize: '0.7rem', color: '#166534', display: 'block', marginTop: '2px' }}>Google Organic Search</span>
                    </div>

                    <div style={{ padding: '14px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                      <span style={{ fontSize: '0.75rem', color: '#1e40af', fontWeight: 600, display: 'block' }}>IMPRESSIONS</span>
                      <strong style={{ fontSize: '1.5rem', color: '#1d4ed8' }}>{summary.totalImpressions.toLocaleString()}</strong>
                      <span style={{ fontSize: '0.7rem', color: '#1e40af', display: 'block', marginTop: '2px' }}>Search SERP Views</span>
                    </div>

                    <div style={{ padding: '14px', background: '#fdf4ff', borderRadius: '8px', border: '1px solid #f5d0fe' }}>
                      <span style={{ fontSize: '0.75rem', color: '#86198f', fontWeight: 600, display: 'block' }}>AVERAGE CTR</span>
                      <strong style={{ fontSize: '1.5rem', color: '#a21caf' }}>{(summary.averageCtr * 100).toFixed(1)}%</strong>
                      <span style={{ fontSize: '0.7rem', color: '#86198f', display: 'block', marginTop: '2px' }}>Clicks per Impression</span>
                    </div>

                    <div style={{ padding: '14px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                      <span style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600, display: 'block' }}>AVG POSITION</span>
                      <strong style={{ fontSize: '1.5rem', color: '#b45309' }}>{summary.averagePosition.toFixed(1)}</strong>
                      <span style={{ fontSize: '0.7rem', color: '#92400e', display: 'block', marginTop: '2px' }}>Average SERP Rank</span>
                    </div>
                  </div>

                  {/* Top Search Queries Table */}
                  {summary.topQueries && summary.topQueries.length > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <h5 style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                        Top Search Queries (Search Console)
                      </h5>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                              <th style={{ padding: '6px 8px' }}>Query</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Clicks</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Impressions</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right' }}>CTR</th>
                              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Avg Pos</th>
                            </tr>
                          </thead>
                          <tbody>
                            {summary.topQueries.slice(0, 5).map((q, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '6px 8px', fontWeight: 500, color: '#1e293b' }}>{q.query}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{q.clicks}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#475569' }}>{q.impressions}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>{(q.ctr * 100).toFixed(1)}%</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right', color: '#b45309' }}>{q.position.toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
